/**
 * Skylite Browser Component - Standardized Template for Vulnerable Boxes
 * 
 * This component provides a consistent browser interface that can be reused
 * for different vulnerable applications. To add a new box, simply:
 * 1. Change the default URL in useEffect
 * 2. Modify the CSS injection in onLoad handler
 * 3. Update any box-specific JavaScript if needed
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { getLearningDashboardUrl } from '../lib/learningDashboardUrl.js';

const TARGET_IP_STORAGE_KEY = 'skylite_target_ip';
const LEGACY_TARGET_IP_STORAGE_KEY = 'sphinx_target_ip';
const SET_BROWSER_TARGET_EVENT = 'skylite:set-browser-target';

const normalizeUrl = (value) => {
  let cleanUrl = String(value || '').trim();
  if (!cleanUrl) return '';
  if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
    cleanUrl = `http://${cleanUrl}`;
  }
  return cleanUrl;
};

/** If user pastes `curl http://...`, extract the URL for the address bar. */
const stripCurlWrapper = (raw) => {
  const s = String(raw || '').trim();
  if (!/^curl\b/i.test(s)) return s;
  const m = s.match(/https?:\/\/[^\s]+/i);
  if (m) return m[0].replace(/[,;'")\]]+$/, '');
  return s.replace(/^curl\b/i, '').replace(/^['"]|['"]$/g, '').trim();
};

/** Scout / vulnapp lab on localhost:8081 or :3002 — origin only, no trailing slash. */
const parseLabOrigin = (src) => {
  if (!src || !String(src).trim()) return null;
  try {
    const u = new URL(normalizeUrl(String(src).trim()));
    if (u.hostname !== 'localhost' && u.hostname !== '127.0.0.1') return null;
    if (u.port === '8081' || u.port === '3002') {
      return `${u.protocol}//${u.hostname}:${u.port}`;
    }
  } catch (_) {}
  return null;
};

const pathAfterLabOrigin = (fullUrl, originNoTrail) => {
  const f = normalizeUrl(fullUrl);
  const o = originNoTrail.replace(/\/$/, '');
  if (f === o || f === `${o}/`) return '/';
  if (!f.startsWith(o)) return '/';
  let tail = f.slice(o.length);
  if (!tail) return '/';
  if (tail.startsWith('?')) tail = `/${tail}`;
  if (!tail.startsWith('/')) tail = `/${tail}`;
  return tail;
};

const BOX01_VERIFY_FLAG_ENDPOINT = '/api/lab-boxes/box-01/verify-flag';

/** Box 1 Scout serves on localhost:8081 only (not vulnapp :3002). */
const isScout8081LabOrigin = (origin) => {
  if (!origin || !String(origin).trim()) return false;
  try {
    const u = new URL(normalizeUrl(String(origin).trim()));
    if (u.hostname !== 'localhost' && u.hostname !== '127.0.0.1') return false;
    return u.port === '8081';
  } catch (_) {
    return false;
  }
};

const isBox01ScoutManifestUrl = (fullUrl, labOrigin) => {
  if (!fullUrl || !labOrigin || !isScout8081LabOrigin(labOrigin)) return false;
  try {
    const u = new URL(normalizeUrl(fullUrl));
    const o = new URL(normalizeUrl(labOrigin));
    if (u.hostname !== o.hostname || u.port !== o.port || u.protocol !== o.protocol) return false;
    const p = (u.pathname || '/').replace(/\/+$/, '') || '/';
    return p === '/backup/manifest.txt';
  } catch (_) {
    return false;
  }
};

/** Normalize pasted / manifest text to `SKYLITE{32-hex}` for verify API. */
const normalizeBox01FlagInput = (raw) => {
  let t = String(raw || '').trim().replace(/^[,;\s]+/, '').trim();
  if (!t) return '';
  const stripPrefix = t.replace(/^\s*SUBMISSION_FLAG\s+/i, '').trim();
  const pick = stripPrefix || t;
  const m = pick.match(/\bSKYLITE\s*\{\s*([0-9a-fA-F\s]+)\}/i);
  if (m) {
    const inner = m[1].replace(/\s+/g, '');
    if (/^[0-9a-f]{32}$/i.test(inner)) return `SKYLITE{${inner.toLowerCase()}}`;
  }
  const compact = pick.replace(/\s+/g, '');
  if (/^[0-9a-fA-F]{32}$/i.test(compact)) return `SKYLITE{${compact.toLowerCase()}}`;
  return t;
};

const extractSubmissionFlagFromManifest = (body) => {
  const text = String(body || '');
  const line = text.split(/\r?\n/).find((l) => /SUBMISSION_FLAG/i.test(l));
  if (!line) return null;
  const out = normalizeBox01FlagInput(line);
  return /^SKYLITE\{[0-9a-f]{32}\}$/.test(out) ? out : null;
};

const BOX01_LEARNED_KEYS = [
  'box01ManifestLearned1',
  'box01ManifestLearned2',
  'box01ManifestLearned3',
  'box01ManifestLearned4',
  'box01ManifestLearned5'
];

const Browser = () => {
  const { currentLanguage, t } = useLanguage();
  const urlInputRef = useRef(null);
  const [url, setUrl] = useState('');
  /** Path only (leading /) when address bar is split for localhost lab. */
  const [pathInput, setPathInput] = useState('/');
  const [browserHistory, setBrowserHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showTargetDialog, setShowTargetDialog] = useState(false);
  const [targetIP, setTargetIP] = useState('');
  const [reconMode, setReconMode] = useState(false);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconError, setReconError] = useState('');
  const [reconResult, setReconResult] = useState(null);
  const [inspectPanelWidth, setInspectPanelWidth] = useState(420);
  const [isResizingInspect, setIsResizingInspect] = useState(false);
  const [box01FlagInput, setBox01FlagInput] = useState('');
  const [box01FlagLoading, setBox01FlagLoading] = useState(false);
  const [box01FlagFeedback, setBox01FlagFeedback] = useState(null);
  /** null = pending fetch; false = fetched manifest but no SUBMISSION_FLAG line; string = flag value */
  const [box01ManifestFlagFetched, setBox01ManifestFlagFetched] = useState(null);
  const [box01ManifestFetchLoading, setBox01ManifestFetchLoading] = useState(false);
  const [box01ManifestFetchFailed, setBox01ManifestFetchFailed] = useState(false);
  const [box01FlagVerifiedSuccess, setBox01FlagVerifiedSuccess] = useState(false);
  /** One-shot green banner after the first successful verify this visit (avoid duplicating the read-only caption). */
  const [box01FlagSuccessToast, setBox01FlagSuccessToast] = useState(false);
  const inspectResizeRef = useRef({
    startX: 0,
    startWidth: 420,
    minWidth: 300,
    maxWidth: 520
  });

  // ============================================================================
  // CONFIGURATION SECTION - Modify these for different vulnerable boxes
  // ============================================================================

  const defaultLearningHubUrl = useMemo(() => getLearningDashboardUrl(), []);

  // Box-specific CSS injection (customize for each box)
  const getBoxSpecificCSS = () => `
    /* Force vulnapp content to expand and cover Sphinx background */
    body, html {
      min-height: 100vh !important;
      height: 100vh !important;
      margin: 0 !important;
      padding: 0 !important;
      position: relative !important;
      z-index: 9998 !important;
      background-color: #0a0a0a !important;
      overflow: hidden !important;
    }
    
    /* Force ALL elements to expand */
    * {
      min-height: fit-content !important;
    }
    
    /* SPECIFICALLY target the .container class */
    .container, .main, .app, .content, .wrapper {
      min-height: 100vh !important;
      height: 100vh !important;
      display: flex !important;
      flex-direction: column !important;
      justify-content: flex-start !important;
      align-items: stretch !important;
      position: relative !important;
      z-index: 9999 !important;
      background-color: #0a0a0a !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    /* Target main containers to expand */
    .main-content, .app-container, [class*="container"], [class*="main"], [class*="content"] {
      min-height: 100vh !important;
      height: 100vh !important;
      background-color: #0a0a0a !important;
    }
    
    /* Force all content areas to expand */
    div, section, main, article, aside, nav {
      min-height: fit-content !important;
      background-color: inherit !important;
    }
    
    /* Override any fixed heights */
    [style*="height"] {
      min-height: 100vh !important;
      height: 100vh !important;
    }
    
    /* Ensure background covers entire space */
    body {
      background-size: cover !important;
      background-position: center !important;
      background-color: #0a0a0a !important;
    }
    
    /* Force the body to take full height */
    html {
      height: 100vh !important;
      background-color: #0a0a0a !important;
    }
    
    /* Remove any margins or padding that might create gaps */
    * {
      box-sizing: border-box !important;
    }
  `;

  // Box-specific JavaScript injection (customize for each box)
  const getBoxSpecificJS = () => {
    // Add any box-specific JavaScript here
    // Example: document.querySelector('.login-form').style.display = 'block';
    return '';
  };

  // ============================================================================
  // END CONFIGURATION SECTION
  // ============================================================================

  const labLockedOrigin = useMemo(
    () => parseLabOrigin(targetIP) || parseLabOrigin(url),
    [targetIP, url]
  );

  const canRunRecon = (value) => {
    try {
      const parsed = new URL(normalizeUrl(value));
      const localHost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
      const allowedPorts = new Set(['8081', '3002']);
      return localHost && allowedPorts.has(parsed.port);
    } catch (_) {
      return false;
    }
  };

  const isSameOriginAsTarget = (value) => {
    try {
      const parsedValue = new URL(normalizeUrl(value));
      const parsedTarget = new URL(normalizeUrl(targetIP || defaultLearningHubUrl));
      return (
        parsedValue.protocol === parsedTarget.protocol &&
        parsedValue.hostname === parsedTarget.hostname &&
        parsedValue.port === parsedTarget.port
      );
    } catch (_) {
      return false;
    }
  };

  const loadPage = useCallback((targetUrl, pushHistory = true) => {
    targetUrl = normalizeUrl(targetUrl);

    if (pushHistory && targetUrl !== browserHistory[historyIndex]) {
      const newHistory = [...browserHistory.slice(0, historyIndex + 1), targetUrl];
      setBrowserHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [browserHistory, historyIndex]);

  useEffect(() => {
    // Prefer a preselected lab target (e.g., Box 01) and fall back to the learning hub.
    let initialTarget = defaultLearningHubUrl;
    try {
      const savedTarget =
        localStorage.getItem(TARGET_IP_STORAGE_KEY) ||
        localStorage.getItem(LEGACY_TARGET_IP_STORAGE_KEY);
      if (savedTarget && savedTarget.trim()) {
        initialTarget = savedTarget.trim();
      }
    } catch (e) {}

    setUrl(initialTarget);
    setTargetIP(initialTarget);
    loadPage(initialTarget, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleSetBrowserTarget = (event) => {
      const nextTarget = event?.detail?.url;
      if (!nextTarget || !String(nextTarget).trim()) return;
      const normalizedTarget = String(nextTarget).trim();
      setUrl(normalizedTarget);
      setTargetIP(normalizedTarget);
      loadPage(normalizedTarget, false);
    };

    window.addEventListener(SET_BROWSER_TARGET_EVENT, handleSetBrowserTarget);
    return () => {
      window.removeEventListener(SET_BROWSER_TARGET_EVENT, handleSetBrowserTarget);
    };
  }, [loadPage]);

  useEffect(() => {
    if (!labLockedOrigin) return;
    setPathInput(pathAfterLabOrigin(url, labLockedOrigin));
  }, [url, labLockedOrigin]);

  const showBox01ManifestFlagPanel = useMemo(
    () => Boolean(labLockedOrigin && isBox01ScoutManifestUrl(url, labLockedOrigin)),
    [url, labLockedOrigin]
  );

  useEffect(() => {
    if (!showBox01ManifestFlagPanel) {
      setBox01FlagFeedback(null);
      setBox01ManifestFlagFetched(null);
      setBox01ManifestFetchLoading(false);
      setBox01ManifestFetchFailed(false);
      setBox01FlagVerifiedSuccess(false);
      setBox01FlagSuccessToast(false);
      setBox01FlagInput('');
    }
  }, [showBox01ManifestFlagPanel]);

  useEffect(() => {
    if (!showBox01ManifestFlagPanel || !url) return undefined;
    let cancelled = false;
    const manifestUrl = normalizeUrl(url);

    setBox01ManifestFetchLoading(true);
    setBox01ManifestFetchFailed(false);
    setBox01ManifestFlagFetched(null);

    (async () => {
      try {
        const res = await fetch(`/api/browser/recon?url=${encodeURIComponent(manifestUrl)}`);
        const rawText = await res.text();
        let data = null;
        try {
          data = JSON.parse(rawText);
        } catch (_) {
          if (!cancelled) setBox01ManifestFetchFailed(true);
          return;
        }
        if (!res.ok || !data?.ok) {
          if (!cancelled) setBox01ManifestFetchFailed(true);
          return;
        }
        const flag = extractSubmissionFlagFromManifest(data.body);
        if (!cancelled) {
          setBox01ManifestFlagFetched(flag || false);
          if (flag) setBox01FlagInput('');
        }
      } catch (_) {
        if (!cancelled) setBox01ManifestFetchFailed(true);
      } finally {
        if (!cancelled) setBox01ManifestFetchLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showBox01ManifestFlagPanel, url]);

  const handleBox01VerifyFlag = async () => {
    const trimmedInput = box01FlagInput.trim();
    const fromManifest =
      typeof box01ManifestFlagFetched === 'string' ? box01ManifestFlagFetched.trim() : '';
    const flag = normalizeBox01FlagInput(trimmedInput || fromManifest);
    if (!flag || !/^SKYLITE\{[0-9a-f]{32}\}$/.test(flag)) {
      setBox01FlagFeedback({ severity: 'error', message: t('box01ManifestFlagInvalid') });
      return;
    }
    setBox01FlagLoading(true);
    setBox01FlagFeedback(null);
    try {
      const res = await fetch(BOX01_VERIFY_FLAG_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flag })
      });
      let data = null;
      try {
        data = await res.json();
      } catch (_) {
        setBox01FlagFeedback({ severity: 'error', message: t('box01ManifestFlagBadResponse') });
        return;
      }
      if (data && data.ok) {
        if (data.progressRecorded === false) {
          setBox01FlagVerifiedSuccess(false);
          setBox01FlagSuccessToast(false);
          setBox01FlagFeedback({
            severity: 'warning',
            message: 'Flag verified, but progress was not saved. Log in and submit again.'
          });
        } else {
          setBox01FlagVerifiedSuccess(true);
          setBox01FlagInput(flag);
          setBox01FlagSuccessToast(true);
        }
      } else {
        if (data?.error === 'login_required_for_progress_save') {
          setBox01FlagFeedback({
            severity: 'warning',
            message: 'Sign in first. Progress is saved only for logged-in users.'
          });
        } else if (data?.error === 'flag_verified_but_progress_not_saved') {
          setBox01FlagFeedback({
            severity: 'warning',
            message: 'Flag verified, but progress was not saved. Submit again after signing in.'
          });
        } else {
          setBox01FlagFeedback({ severity: 'error', message: t('box01ManifestFlagErrorGeneric') });
        }
      }
    } catch (_) {
      setBox01FlagFeedback({ severity: 'error', message: t('box01ManifestFlagNetworkError') });
    } finally {
      setBox01FlagLoading(false);
    }
  };

  const handleTargetSubmit = () => {
    if (targetIP.trim()) {
      const cleanIP = normalizeUrl(targetIP);
      localStorage.setItem(TARGET_IP_STORAGE_KEY, targetIP.trim());
      localStorage.removeItem(LEGACY_TARGET_IP_STORAGE_KEY);
      setUrl(cleanIP);
      setShowTargetDialog(false);
      loadPage(cleanIP, false);
    }
  };

  const handleUrlSubmit = (e) => {
    e.preventDefault();
    const cleaned = stripCurlWrapper(url);
    if (cleaned !== url) {
      setUrl(cleaned);
    }
    loadPage(cleaned);
  };

  const handleLabPathSubmit = (e) => {
    e.preventDefault();
    if (!labLockedOrigin) return;
    const base = labLockedOrigin.replace(/\/$/, '');
    let raw = stripCurlWrapper(pathInput.trim());
    if (/^https?:\/\//i.test(raw)) {
      const nu = normalizeUrl(raw);
      if (nu.startsWith(base) && (nu.length === base.length || ['/', '?'].includes(nu[base.length]))) {
        raw = nu.slice(base.length) || '/';
      } else {
        raw = '/';
      }
    }
    let p = (raw || '/').trim() || '/';
    if (!p.startsWith('/')) p = `/${p}`;
    const full = `${base}${p}`;
    setUrl(full);
    loadPage(full);
  };

  const handleBack = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      loadPage(browserHistory[newIndex], false);
    }
  };

  const handleForward = () => {
    if (historyIndex < browserHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      loadPage(browserHistory[newIndex], false);
    }
  };

  const handleRefresh = () => {
    // Refresh always returns to the configured box target URL.
    const fallbackTarget = normalizeUrl(targetIP) || defaultLearningHubUrl;
    if (fallbackTarget) {
      setUrl(fallbackTarget);
      loadPage(fallbackTarget, false);
    }
  };

  const runRecon = async (probeUrl = url) => {
    const normalizedProbeUrl = normalizeUrl(probeUrl);
    if (!canRunRecon(normalizedProbeUrl) || !isSameOriginAsTarget(normalizedProbeUrl)) {
      setReconError('Inspect only works for the active running box URL.');
      setReconResult(null);
      return;
    }

    setReconLoading(true);
    setReconError('');
    try {
      const response = await fetch(`/api/browser/recon?url=${encodeURIComponent(normalizedProbeUrl)}`);
      const rawText = await response.text();
      let data = null;
      try {
        data = JSON.parse(rawText);
      } catch (_) {
        if (!response.ok) {
          throw new Error(rawText || 'Inspect request failed.');
        }
        data = {
          ok: true,
          url: normalizedProbeUrl,
          status: response.status,
          statusText: response.statusText,
          contentType: response.headers.get('content-type') || 'unknown',
          headers: {},
          body: rawText.slice(0, 50000)
        };
      }

      if (!response.ok || !data?.ok) {
        throw new Error(data?.error || 'Inspect request failed.');
      }
      setReconResult(data);
    } catch (error) {
      setReconResult(null);
      setReconError(error?.message || 'Inspect request failed.');
    } finally {
      setReconLoading(false);
    }
  };

  const handleInspectResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const parent = e.currentTarget.parentElement;
    if (!parent) return;
    const parentWidth = parent.getBoundingClientRect().width;

    inspectResizeRef.current = {
      startX: e.clientX,
      startWidth: inspectPanelWidth,
      minWidth: 300,
      maxWidth: Math.max(520, Math.floor(parentWidth * 0.7))
    };
    setIsResizingInspect(true);
  };

  useEffect(() => {
    if (!isResizingInspect) return undefined;

    const onMouseMove = (event) => {
      const { startX, startWidth, minWidth, maxWidth } = inspectResizeRef.current;
      const deltaX = startX - event.clientX;
      const nextWidth = Math.round(startWidth + deltaX);
      const clamped = Math.min(maxWidth, Math.max(minWidth, nextWidth));
      setInspectPanelWidth(clamped);
    };

    const onMouseUp = () => {
      setIsResizingInspect(false);
    };

    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isResizingInspect]);

  // Get placeholder text based on current language
  const getPlaceholderText = () => {
    switch (currentLanguage) {
      case 'es':
        return 'Escriba IP de la Víctima o Dirección Web Aquí';
      case 'fr':
        return 'Tapez l\'IP de la Victime ou l\'Adresse Web Ici';
      case 'de':
        return 'Geben Sie die IP des Opfers oder Webadresse hier ein';
      case 'it':
        return 'Digita l\'IP della Vittima o Indirizzo Web Qui';
      case 'pt':
        return 'Digite o IP da Vítima ou Endereço Web Aqui';
      case 'ru':
        return 'Введите IP жертвы или веб-адрес здесь';
      case 'zh':
        return '在此输入受害者IP或网址';
      case 'ja':
        return '被害者のIPまたはウェブアドレスをここに入力';
      case 'ko':
        return '여기에 피해자 IP 또는 웹주소 입력';
      case 'ar':
        return 'اكتب IP الضحية أو عنوان الويب هنا';
      default:
        return 'Type Victim IP or Web Address Here';
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'linear-gradient(180deg, #10192f 0%, #0a1020 100%)', overflow: 'hidden' }}>
      {/* Target IP Dialog */}
      <Dialog open={showTargetDialog} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle>🎯 {t('connectToHackableBox')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('enterTargetIP')}
          </Typography>
          <TextField
            fullWidth
            label={t('targetIPAddress')}
            value={targetIP}
            onChange={(e) => setTargetIP(e.target.value)}
            placeholder="e.g., 192.168.1.100"
            variant="outlined"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTargetSubmit} variant="contained" color="primary">
            {t('connect')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Searchable-style browser chrome */}
      <Box sx={{ 
        backgroundColor: '#0f1a31', 
        display: 'flex',
        alignItems: 'center',
        height: '38px',
        px: 2,
        gap: 1,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        borderBottom: '1px solid rgba(122, 208, 255, 0.3)',
        flexShrink: 0
      }}>
        {/* Navigation Buttons - Left side */}
        <IconButton 
          onClick={handleBack} 
          disabled={historyIndex <= 0} 
          size="small"
          sx={{ 
            color: historyIndex <= 0 ? '#6e7a94' : '#c7defa',
            '&:hover': { backgroundColor: 'transparent' },
            minWidth: '24px',
            minHeight: '24px',
            p: 0.5,
            mr: 1,
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none'
          }}
        >
          <ArrowBackIcon sx={{ fontSize: '18px', color: historyIndex <= 0 ? '#6e7a94' : '#c7defa' }} />
        </IconButton>
        <IconButton 
          onClick={handleForward} 
          disabled={historyIndex >= browserHistory.length - 1} 
          size="small"
          sx={{ 
            color: historyIndex >= browserHistory.length - 1 ? '#6e7a94' : '#c7defa',
            '&:hover': { backgroundColor: 'transparent' },
            minWidth: '24px',
            minHeight: '24px',
            p: 0.5,
            mr: 1,
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none'
          }}
        >
          <ArrowForwardIcon sx={{ fontSize: '18px', color: historyIndex >= browserHistory.length - 1 ? '#6e7a94' : '#c7defa' }} />
        </IconButton>
        <IconButton 
          onClick={handleRefresh}
          size="small"
          sx={{ 
            color: '#92d8ff',
            '&:hover': { backgroundColor: 'transparent' },
            minWidth: '24px',
            minHeight: '24px',
            p: 0.5,
            mr: 2,
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none'
          }}
        >
          <RefreshIcon sx={{ fontSize: '18px', color: '#92d8ff' }} />
        </IconButton>

        {/* White Address Bar - Center */}
        <Box
          sx={{
            width: '60%',
            mx: 2,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#f4f9ff',
            borderRadius: '6px',
            border: '1px solid #9ad8ff',
            px: 1,
            py: 0.5,
            '&:hover': {
              borderColor: '#d0d0d0',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }
          }}
          onMouseDown={() => {
            urlInputRef.current?.focus();
          }}
        >
          <SearchIcon sx={{ 
            fontSize: '16px', 
            color: '#48638c', 
            mr: 1 
          }} />
          {labLockedOrigin ? (
            <Box
              component="form"
              onSubmit={handleLabPathSubmit}
              sx={{ display: 'flex', flex: 1, alignItems: 'center', minWidth: 0, gap: 0.25 }}
            >
              <Typography
                component="span"
                title={labLockedOrigin}
                sx={{
                  flexShrink: 0,
                  fontSize: '13px',
                  color: '#1a3a5c',
                  fontWeight: 600,
                  userSelect: 'none',
                  maxWidth: '52%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {labLockedOrigin}
              </Typography>
              <TextField
                fullWidth
                inputRef={urlInputRef}
                value={pathInput}
                onChange={(e) => setPathInput(e.target.value)}
                placeholder="/health"
                variant="standard"
                name="skylite-browser-lab-path"
                autoComplete="off"
                spellCheck={false}
                InputProps={{
                  disableUnderline: true,
                  style: {
                    fontSize: '14px',
                    color: '#333333',
                    fontWeight: '400'
                  }
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiInputBase-root': { backgroundColor: 'transparent' },
                  '& .MuiInputBase-input': { fontSize: '14px', color: '#333333' },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#999999',
                    opacity: 1,
                    fontSize: '14px'
                  }
                }}
              />
            </Box>
          ) : (
            <form onSubmit={handleUrlSubmit} style={{ display: 'flex', flex: 1, position: 'relative' }}>
              <TextField
                fullWidth
                inputRef={urlInputRef}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={getPlaceholderText()}
                variant="standard"
                name="skylite-browser-url"
                autoComplete="url"
                spellCheck={false}
                InputProps={{
                  disableUnderline: true,
                  style: {
                    fontSize: '14px',
                    color: url ? '#333333' : '#999999',
                    fontWeight: '400'
                  }
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'transparent'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#999999',
                    opacity: 1,
                    fontSize: '14px'
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    color: url ? '#333333' : '#999999'
                  },
                  '& .MuiInputBase-input::-webkit-input-placeholder': {
                    color: '#999999',
                    opacity: 1
                  },
                  '& .MuiInputBase-input::-moz-placeholder': {
                    color: '#999999',
                    opacity: 1
                  },
                  '& .MuiInputBase-input:-ms-input-placeholder': {
                    color: '#999999',
                    opacity: 1
                  }
                }}
              />
              {!url && (
                <Typography
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#999999',
                    fontSize: '14px',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                >
                  {getPlaceholderText()}
                </Typography>
              )}
            </form>
          )}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => {
            const nextMode = !reconMode;
            setReconMode(nextMode);
            if (nextMode) runRecon(url);
          }}
          size="small"
          variant={reconMode ? 'contained' : 'outlined'}
          sx={{
            minWidth: '72px',
            height: '24px',
            mr: 1,
            color: reconMode ? '#001d36' : '#92d8ff',
            borderColor: '#92d8ff',
            backgroundColor: reconMode ? '#92d8ff' : 'transparent',
            '&:hover': {
              borderColor: '#92d8ff',
              backgroundColor: reconMode ? '#7cc7f0' : 'rgba(146, 216, 255, 0.12)'
            }
          }}
        >
          Inspect
        </Button>
      </Box>

      {/* Main Content Area - Two-tone like the image */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0
      }}>
        {/* Searchable strip */}
        <Box sx={{ 
          height: '34px',
          backgroundColor: '#eaf3ff',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          gap: 1
        }} />

        {showBox01ManifestFlagPanel && (
          <Box
            sx={{
              flexShrink: 0,
              position: 'relative',
              zIndex: 5,
              isolation: 'isolate',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #c5d9f0',
              backgroundColor: '#f0f7ff',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85)',
              maxHeight: 'min(48vh, 480px)',
              overflowY: 'auto'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d2744' }}>
              {t('box01ManifestFlagPanelTitle')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, color: '#1a3555', lineHeight: 1.5, maxWidth: '900px' }}>
              {t('box01ManifestFlagPanelBody')}
            </Typography>

            {box01ManifestFetchLoading && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  {t('box01ManifestFlagLoading')}
                </Typography>
              </Stack>
            )}

            {box01ManifestFetchFailed && !box01ManifestFetchLoading && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {t('box01ManifestFlagLoadError')}
              </Alert>
            )}

            {!box01ManifestFetchLoading && !box01ManifestFetchFailed && box01ManifestFlagFetched === false && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                {t('box01ManifestFlagMissing')}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleBox01VerifyFlag();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ mt: 2 }}
            >
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#0d2744', mb: 0.75 }}>
                {t('box01ManifestFlagSubmitHint')}
              </Typography>
              <Stack direction="column" spacing={1.25} sx={{ width: '100%', maxWidth: 560 }}>
                <Box
                  component="input"
                  type="text"
                  name="skylite-box01-flag"
                  autoComplete="off"
                  spellCheck={false}
                  aria-label={t('box01ManifestFlagPlaceholder')}
                  value={box01FlagInput}
                  disabled={box01FlagVerifiedSuccess}
                  onChange={(e) => setBox01FlagInput(e.target.value)}
                  onBlur={() => {
                    if (box01FlagVerifiedSuccess) return;
                    setBox01FlagInput((prev) => {
                      const n = normalizeBox01FlagInput(prev);
                      return n && /^SKYLITE\{[0-9a-f]{32}\}$/.test(n) ? n : prev;
                    });
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  placeholder={t('box01ManifestFlagPlaceholder')}
                  sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    px: 1.5,
                    py: 1.125,
                    fontSize: '0.9rem',
                    lineHeight: 1.35,
                    fontFamily: 'ui-monospace, Consolas, monospace',
                    color: '#052038',
                    border: '1px solid #b8d4f0',
                    borderRadius: 1,
                    backgroundColor: box01FlagVerifiedSuccess ? 'rgba(13,39,68,0.06)' : 'rgba(255,255,255,0.98)',
                    outline: 'none',
                    cursor: box01FlagVerifiedSuccess ? 'default' : 'text',
                    '&:focus': {
                      borderColor: '#1976d2',
                      boxShadow: box01FlagVerifiedSuccess ? 'none' : '0 0 0 1px rgba(25, 118, 210, 0.35)'
                    },
                    '&:disabled': {
                      opacity: 0.95,
                      WebkitTextFillColor: '#052038'
                    }
                  }}
                />
                {box01FlagVerifiedSuccess && (
                  <Typography variant="caption" sx={{ color: '#0d2744', lineHeight: 1.4 }}>
                    {t('box01ManifestFlagInputLockedAfterSuccess')}
                  </Typography>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={box01FlagLoading || box01FlagVerifiedSuccess}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ alignSelf: 'flex-start', minWidth: '140px' }}
                >
                  {box01FlagLoading ? <CircularProgress size={20} color="inherit" /> : t('box01ManifestFlagSubmit')}
                </Button>
              </Stack>
            </Box>

            {box01FlagSuccessToast && (
              <Alert
                severity="success"
                sx={{ mt: 1.5 }}
                onClose={() => setBox01FlagSuccessToast(false)}
              >
                {t('box01ManifestFlagSuccess')}
              </Alert>
            )}

            {box01FlagFeedback && (
              <Alert severity={box01FlagFeedback.severity} sx={{ mt: 1.5 }} onClose={() => setBox01FlagFeedback(null)}>
                {box01FlagFeedback.message}
              </Alert>
            )}

            {box01FlagVerifiedSuccess && (
              <Box sx={{ mt: 2, pb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d2744' }}>
                  {t('box01ManifestLearnedTitle')}
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.25, color: '#1a3555' }}>
                  {BOX01_LEARNED_KEYS.map((key) => (
                    <Typography key={key} component="li" variant="body2" sx={{ mb: 0.65, lineHeight: 1.45 }}>
                      {t(key)}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Bottom Section - Large white area with iframe */}
        <Box sx={{ 
          flex: 1, 
          minHeight: 0,
          backgroundColor: '#fff',
          overflow: 'hidden',
          position: 'relative', // Added for background overlay positioning
          display: 'flex',
          zIndex: 0
        }}>
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            {/* Background overlay removed - CSS injection will handle Sphinx background coverage */}
            {url ? (
            <iframe
              key={url}
              src={url}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
                position: 'relative'
              }}
              title="Browser Content"
              allow="fullscreen"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
              referrerPolicy="no-referrer"
              onLoad={(e) => {
                console.log('Iframe loaded:', url);
                console.log('Iframe element:', e.target);
                
                // Inject CSS to force content to fill the iframe and cover Sphinx background
                try {
                  const iframe = e.target;
                  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                  
                  console.log('Iframe document:', iframeDoc);
                  console.log('Iframe document head:', iframeDoc.head);
                  
                  // Create and inject CSS to expand the vulnapp content
                  const style = iframeDoc.createElement('style');
                  style.textContent = getBoxSpecificCSS();
                  
                  iframeDoc.head.appendChild(style);
                  console.log('CSS injected to expand vulnapp content');
                  
                  // Execute box-specific JavaScript if any
                  if (getBoxSpecificJS()) {
                    const script = iframeDoc.createElement('script');
                    script.textContent = getBoxSpecificJS();
                    iframeDoc.head.appendChild(script);
                    console.log('Box-specific JavaScript injected');
                  }
                } catch (error) {
                  console.log('CSS injection failed (cross-origin restriction):', error.message);
                }

                if (reconMode) {
                  runRecon(url);
                }
              }}
              onError={(e) => console.error('Iframe error:', e)}
            />
            ) : (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#666',
              fontSize: '16px'
            }}>
              Enter a URL above to browse
            </Box>
            )}
          </Box>
          {reconMode && (
            <>
              <Box
                onMouseDown={handleInspectResizeStart}
                sx={{
                  width: '8px',
                  cursor: 'col-resize',
                  backgroundColor: isResizingInspect ? '#4b79aa' : '#203a5a',
                  '&:hover': { backgroundColor: '#4b79aa' }
                }}
              />
              <Box
                sx={{
                  width: `${inspectPanelWidth}px`,
                  minWidth: '300px',
                  backgroundColor: '#091322',
                  color: '#d8e8ff',
                  borderLeft: '1px solid #2f4d77',
                  p: 1,
                  overflow: 'auto'
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', color: '#9fc8ef' }}>
                  Inspect HTML (current box page)
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#7fb6e8', mt: 0.5 }}>
                  URL: {normalizeUrl(url)}
                </Typography>

                {reconLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption">Loading page source...</Typography>
                  </Box>
                )}

                {reconError && (
                  <Typography variant="caption" sx={{ mt: 1, color: '#ffb4b4', display: 'block' }}>
                    {reconError}
                  </Typography>
                )}

                {reconResult && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: '#9fc8ef' }}>
                      {reconResult.status} {reconResult.statusText}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: '#8fd3ff', mt: 0.5 }}>
                      Content-Type: {reconResult.contentType}
                    </Typography>
                    <Typography
                      component="pre"
                      sx={{
                        fontSize: '11px',
                        whiteSpace: 'pre-wrap',
                        m: 0,
                        mt: 1,
                        color: '#d8e8ff',
                        wordBreak: 'break-word'
                      }}
                    >
                      {reconResult.body}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
          {isResizingInspect && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 20,
                cursor: 'col-resize',
                backgroundColor: 'transparent'
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Browser; 